/**
 * Pickup Products のポップアップ表示。
 *
 * 「今まさに複数の商品を見比べている人」に絞って出すため、
 * 閲覧した商品ページ数がしきい値に達したときだけ表示する。
 *
 * 閲覧数はセッション単位で数える。sessionStorage はタブごとに分かれてしまい、
 * 商品を別タブで開く人が永久に条件を満たさなくなるため、localStorage に
 * 最終閲覧時刻を持たせ、一定時間空いたらリセットする方式にしている。
 * 「×」で閉じた記録も同じ器に入れるので、時間が空けばまた出るようになる。
 *
 * data-min-views        表示に必要な商品ページ閲覧数
 * data-session-minutes  この時間だけ間隔が空いたらセッションを切り替える（分）
 * data-mobile-mode      スマホでの振る舞い hide|bar（bar のとき表示する）
 * data-product-id       いま見ている商品のID（重複カウント防止用）
 * data-columns          開いたときにPCで横に並べる枚数
 * data-columns-mobile   開いたときにスマホで横に並べる枚数
 * data-ui-version       見た目の版。GA4 で前後を比べるため widget-analytics.js が読む
 */
if (!customElements.get('pickup-products-popup')) {
  /** 画面を動かした直後のタップは、開く操作とみなさない（ミリ秒）。 */
  const SCROLL_GUARD_MS = 400;

  /** 指がこれ以上動いたら、タップではなくスワイプとみなす（px）。 */
  const SWIPE_SLOP_PX = 10;

  /** スクロールが止まってから、また出すまでの時間（ミリ秒）。 */
  const SCROLL_IDLE_MS = 300;

  customElements.define(
    'pickup-products-popup',
    class PickupProductsPopup extends HTMLElement {
      connectedCallback() {
        this.storageKey = 'sanngai:pickup-popup';
        this.minViews = parseInt(this.dataset.minViews, 10) || 2;
        this.sessionMs = (parseInt(this.dataset.sessionMinutes, 10) || 30) * 60 * 1000;
        this.mobileMode = this.dataset.mobileMode || 'hide';
        this.columns = parseInt(this.dataset.columns, 10) || 4;
        this.columnsMobile = parseInt(this.dataset.columnsMobile, 10) || 2;

        this.stack = this.querySelector('[data-popup-open]');
        this.panel = this.querySelector('[data-popup-panel]');
        this.scroller = this.querySelector('[data-popup-scroller]');
        this.grid = this.querySelector('[data-popup-grid]');
        this.dots = this.querySelector('[data-popup-dots]');
        this.dismissButton = this.querySelector('[data-popup-dismiss]');

        if (this.stack) {
          this.stack.addEventListener('click', () => {
            if (this.isScrolling()) return;
            this.expand();
          });
          this.watchTouch();
        }

        // ページを動かしている間のタップは無視する。指を滑らせた勢いで
        // 山に触れてしまうのが誤タップのいちばん多い原因。
        this.lastScrollAt = 0;
        this.onScroll = () => {
          this.lastScrollAt = Date.now();
          // 畳んでいる間だけ消す。開いているのは操作中なので消さない。
          if (!this.isExpanded()) this.classList.add('pickup-popup--scrolling');
          clearTimeout(this.scrollIdleTimer);
          this.scrollIdleTimer = setTimeout(() => {
            this.classList.remove('pickup-popup--scrolling');
          }, SCROLL_IDLE_MS);
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        // 「×」は状態で意味が変わる。
        //   開いている … パネルをたたんで山に戻す
        //   たたんでいる … その訪問中はもう出さない
        if (this.dismissButton) {
          this.dismissButton.addEventListener('click', () => {
            if (this.isExpanded()) this.collapse();
            else this.dismiss();
          });
          this.syncDismissLabel();
        }

        this.onKeydown = (event) => {
          if (event.key === 'Escape' && this.isExpanded()) this.collapse();
        };
        document.addEventListener('keydown', this.onKeydown);

        // テーマエディタでは条件に関係なく出す。でないと編集できない。
        if (window.Shopify && Shopify.designMode) {
          this.hidden = false;
          return;
        }

        // 動作確認用。URLに ?pickup_preview=1 を付けると、閲覧数や「×」の記録に
        // かかわらず表示する。開発者ツールを開けないスマホでも確認できるようにするため。
        // 記録自体は通常どおり行うので、確認後に普通のURLで開けば本来の動きに戻る。
        if (new URLSearchParams(window.location.search).get('pickup_preview') === '1') {
          this.recordView();
          this.hidden = false;
          return;
        }

        const state = this.recordView();
        if (!this.shouldShow(state)) return;

        this.hidden = false;
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        clearTimeout(this.scrollIdleTimer);
        if (this.scrollHandler && this.scroller) {
          this.scroller.removeEventListener('scroll', this.scrollHandler);
        }
      }

      /** 「×」が何をするボタンなのかを、いまの状態に合わせて書き換える。 */
      syncDismissLabel() {
        if (!this.dismissButton) return;
        const key = this.isExpanded() ? 'labelCollapse' : 'labelDismiss';
        const label = this.dismissButton.dataset[key];
        if (label) this.dismissButton.setAttribute('aria-label', label);
      }

      /** 直前にページが動いていたか。 */
      isScrolling() {
        if (this.swiped) return true;
        return Date.now() - (this.lastScrollAt || 0) < SCROLL_GUARD_MS;
      }

      /**
       * 山の上で指が動いたら、その一連の操作はスワイプとして扱う。
       * 慣性スクロールを指で止めるときは scroll が出ないことがあるため、
       * 時間のガードだけでは取りこぼす。
       */
      watchTouch() {
        let startX = 0;
        let startY = 0;

        this.stack.addEventListener(
          'touchstart',
          (event) => {
            const touch = event.touches[0];
            if (!touch) return;
            startX = touch.clientX;
            startY = touch.clientY;
            this.swiped = false;
          },
          { passive: true }
        );

        this.stack.addEventListener(
          'touchmove',
          (event) => {
            const touch = event.touches[0];
            if (!touch) return;
            const moved = Math.abs(touch.clientX - startX) + Math.abs(touch.clientY - startY);
            if (moved > SWIPE_SLOP_PX) this.swiped = true;
          },
          { passive: true }
        );
      }

      isMobile() {
        return !window.matchMedia('(min-width: 750px)').matches;
      }

      isExpanded() {
        return !!this.panel && !this.panel.hidden;
      }

      /** たたんだ山から、画像を並べたパネルへ切り替える。 */
      expand() {
        if (!this.panel || !this.stack) return;
        this.stack.hidden = true;
        this.stack.setAttribute('aria-expanded', 'true');
        this.panel.hidden = false;
        // 「×」は開いている間だけ出す（CSS側で切り替える）
        this.classList.add('pickup-popup--open');
        this.classList.remove('pickup-popup--scrolling');
        this.syncDismissLabel();
        this.buildDots();
      }

      collapse() {
        if (!this.panel || !this.stack) return;
        this.panel.hidden = true;
        this.stack.hidden = false;
        this.stack.setAttribute('aria-expanded', 'false');
        this.classList.remove('pickup-popup--open');
        this.syncDismissLabel();
        this.stack.focus();
      }

      /** 「×」。その訪問中はもう出さない。時間が空けばまた出る。 */
      dismiss() {
        this.hidden = true;
        const state = this.readState() || { viewed: [] };
        state.dismissed = true;
        state.lastSeen = Date.now();
        this.writeState(state);
      }

      /**
       * 入りきらない枚数があるときだけ、現在地のドットを出す。
       * 1ページに収まるならドットは意味がないので出さない。
       */
      buildDots() {
        if (!this.dots || !this.scroller || !this.grid) return;

        const items = this.grid.children.length;
        const perColumn = this.isMobile() ? 2 : 1;
        const columns = this.isMobile() ? this.columnsMobile : this.columns;
        const perPage = columns * perColumn;
        const pages = Math.ceil(items / perPage);

        this.dots.innerHTML = '';
        if (pages <= 1) {
          this.dots.hidden = true;
          return;
        }

        this.dots.hidden = false;
        for (let i = 0; i < pages; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'pickup-popup__dot';
          dot.setAttribute('aria-label', i + 1 + 'ページ目へ');
          dot.setAttribute('aria-current', i === 0 ? 'true' : 'false');
          dot.addEventListener('click', () => {
            this.scroller.scrollTo({ left: this.scroller.clientWidth * i, behavior: 'smooth' });
          });
          this.dots.appendChild(dot);
        }

        if (!this.scrollHandler) {
          this.scrollHandler = () => {
            const width = this.scroller.clientWidth || 1;
            const current = Math.round(this.scroller.scrollLeft / width);
            Array.from(this.dots.children).forEach((dot, index) => {
              dot.setAttribute('aria-current', index === current ? 'true' : 'false');
            });
          };
          this.scroller.addEventListener('scroll', this.scrollHandler, { passive: true });
        }
      }

      readState() {
        try {
          const raw = localStorage.getItem(this.storageKey);
          return raw ? JSON.parse(raw) : null;
        } catch (error) {
          // プライベートウィンドウなどで localStorage が使えないことがある。
          return null;
        }
      }

      writeState(state) {
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(state));
        } catch (error) {
          /* 保存できなくても表示自体は成立させる */
        }
      }

      /**
       * いま見ている商品を閲覧済みとして記録し、更新後の状態を返す。
       * 同じ商品の再読み込みで数が増えないよう、商品IDの集合で数える。
       */
      recordView() {
        const now = Date.now();
        let state = this.readState();

        if (!state || !state.lastSeen || now - state.lastSeen > this.sessionMs) {
          state = { viewed: [], dismissed: false };
        }

        const productId = this.dataset.productId;
        if (productId && !state.viewed.includes(productId)) {
          state.viewed.push(productId);
          // 際限なく溜めない
          if (state.viewed.length > 50) state.viewed.shift();
        }

        state.lastSeen = now;
        this.writeState(state);
        return state;
      }

      shouldShow(state) {
        if (state.dismissed) return false;
        if (this.isMobile() && this.mobileMode !== 'bar') return false;
        return state.viewed.length >= this.minViews;
      }
    }
  );
}
